/**
 * Phase T1 — Tenant identity (single-tenant branding) settings card.
 *
 * Lives inside `AdminSystemSettingsPage`. Super-admin only — the parent page
 * is already gated to super_admin so the card itself doesn't need an
 * additional role check. All form sections (Identity / Address / Contact /
 * Tax / Logo) share the same save button; per-field errors come back from
 * the backend `field_errors` map under HTTP 400.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatApiError, getBackendBase } from "@/lib/api";
import PhoneInput from "@/components/PhoneInput";
import { useTenant } from "@/context/TenantContext";
import { toast } from "sonner";
import { Building2, Upload, Trash2, ImageIcon } from "lucide-react";

const EMPTY_FORM = {
    legal_name: "",
    display_name: "",
    dba: "",
    address: {
        line1: "",
        line2: "",
        city: "",
        state: "",
        zip: "",
        country: "US",
    },
    phone: "",
    reply_to_email: "",
    website: "",
    ein: "",
};

function pickForm(tenant) {
    if (!tenant) return EMPTY_FORM;
    const addr = tenant.address || {};
    return {
        legal_name: tenant.legal_name || "",
        display_name: tenant.display_name || "",
        dba: tenant.dba || "",
        address: {
            line1: addr.line1 || "",
            line2: addr.line2 || "",
            city: addr.city || "",
            state: addr.state || "",
            zip: addr.zip || "",
            country: addr.country || "US",
        },
        phone: tenant.phone || "",
        reply_to_email: tenant.reply_to_email || "",
        website: tenant.website || "",
        ein: tenant.ein || "",
    };
}

function diffPatch(orig, next) {
    // Compute a minimal PATCH body. We only include fields the user touched.
    const out = {};
    for (const k of ["legal_name", "display_name", "dba", "phone", "reply_to_email", "website", "ein"]) {
        if ((orig[k] || "") !== (next[k] || "")) out[k] = next[k] || null;
    }
    // Address: send when any sub-field differs.
    const a = orig.address || {};
    const b = next.address || {};
    let addrDirty = false;
    for (const k of ["line1", "line2", "city", "state", "zip", "country"]) {
        if ((a[k] || "") !== (b[k] || "")) { addrDirty = true; break; }
    }
    if (addrDirty) {
        const anyValue = ["line1", "city", "state", "zip"].some((k) => (b[k] || "").trim());
        out.address = anyValue ? {
            line1: b.line1 || null,
            line2: b.line2 || null,
            city: b.city || null,
            state: b.state || null,
            zip: b.zip || null,
            country: b.country || "US",
        } : null;
    }
    return out;
}

export function TenantSettingsCard() {
    const { tenant: cachedTenant, refreshTenant } = useTenant();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [original, setOriginal] = useState(EMPTY_FORM);
    const [form, setForm] = useState(EMPTY_FORM);
    const [errors, setErrors] = useState({});
    const [logoRecord, setLogoRecord] = useState(null);
    const [logoBust, setLogoBust] = useState(0);
    const fileInputRef = useRef(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/system/tenant");
            const t = data?.tenant || {};
            const next = pickForm(t);
            setForm(next);
            setOriginal(next);
            setLogoRecord(t.logo || null);
            setErrors({});
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const setField = (key, value) => {
        setForm((prev) => ({ ...prev, [key]: value }));
        if (errors[key]) {
            setErrors((prev) => { const c = { ...prev }; delete c[key]; return c; });
        }
    };

    const setAddrField = (key, value) => {
        setForm((prev) => ({ ...prev, address: { ...prev.address, [key]: value } }));
        const errKey = `address.${key}`;
        if (errors[errKey]) {
            setErrors((prev) => { const c = { ...prev }; delete c[errKey]; return c; });
        }
    };

    const dirty = JSON.stringify(form) !== JSON.stringify(original);

    const onSave = async (e) => {
        e?.preventDefault?.();
        setErrors({});
        const patch = diffPatch(original, form);
        if (Object.keys(patch).length === 0) {
            toast.message("Nothing to save.");
            return;
        }
        setSaving(true);
        try {
            const { data } = await api.patch("/admin/system/tenant", patch);
            const next = pickForm(data?.tenant || {});
            setForm(next);
            setOriginal(next);
            setLogoRecord(data?.tenant?.logo || null);
            toast.success("Tenant settings saved.");
            refreshTenant();
        } catch (err) {
            const fieldErrors = err?.response?.data?.detail?.field_errors;
            if (fieldErrors && typeof fieldErrors === "object") {
                setErrors(fieldErrors);
                toast.error("Please fix the highlighted fields.");
            } else {
                toast.error(formatApiError(err));
            }
        } finally {
            setSaving(false);
        }
    };

    const onLogoChange = async (ev) => {
        const f = ev.target.files?.[0];
        if (!f) return;
        if (f.size > 2 * 1024 * 1024) {
            toast.error(`Logo too large (${(f.size / 1024 / 1024).toFixed(2)} MB). Max is 2 MB.`);
            ev.target.value = "";
            return;
        }
        const fd = new FormData();
        fd.append("logo", f);
        try {
            const { data } = await api.post("/admin/system/tenant/logo", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setLogoRecord(data?.logo || null);
            setLogoBust((n) => n + 1);
            toast.success("Logo uploaded.");
            refreshTenant();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            ev.target.value = "";
        }
    };

    const onLogoDelete = async () => {
        if (!logoRecord) return;
        if (!window.confirm("Delete the tenant logo?")) return;
        try {
            await api.delete("/admin/system/tenant/logo");
            setLogoRecord(null);
            setLogoBust((n) => n + 1);
            toast.success("Logo removed.");
            refreshTenant();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const errFor = (key) => (errors[key] ? (
        <div className="text-xs text-red-700 mt-1" data-testid={`tenant-error-${key.replace(/\./g, "-")}`}>
            {errors[key]}
        </div>
    ) : null);

    const logoSrc = logoRecord
        ? `${getBackendBase()}/api/public/tenant/logo?b=${logoBust}`
        : null;

    return (
        <Card className="lg:col-span-2" data-testid="tenant-settings-card">
            <CardHeader>
                <div className="flex items-center gap-2">
                    <Building2 size={16} className="text-blue-900" />
                    <CardTitle className="font-display text-xl">Tenant identity</CardTitle>
                </div>
                <CardDescription>
                    Branding used on invoices, contracts, emails, and the customer portal.
                    Leave blank to fall back to the RentFlow defaults.
                </CardDescription>
            </CardHeader>
            <CardContent>
                <form onSubmit={onSave} className="space-y-8" data-testid="tenant-settings-form">
                    {/* Identity */}
                    <section>
                        <h3 className="text-sm font-semibold text-slate-900 mb-3 tracking-wide uppercase">Identity</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <Label htmlFor="tn-legal-name">Legal name</Label>
                                <Input id="tn-legal-name" data-testid="tenant-legal-name-input"
                                       disabled={loading} value={form.legal_name}
                                       onChange={(e) => setField("legal_name", e.target.value)}
                                       placeholder="All About Trucking LLC" />
                                {errFor("legal_name")}
                            </div>
                            <div>
                                <Label htmlFor="tn-display-name">Display name</Label>
                                <Input id="tn-display-name" data-testid="tenant-display-name-input"
                                       disabled={loading} value={form.display_name}
                                       onChange={(e) => setField("display_name", e.target.value)}
                                       placeholder="All About Trucking" />
                                {errFor("display_name")}
                            </div>
                            <div className="sm:col-span-2">
                                <Label htmlFor="tn-dba">DBA <span className="text-slate-400">(optional)</span></Label>
                                <Input id="tn-dba" data-testid="tenant-dba-input"
                                       disabled={loading} value={form.dba}
                                       onChange={(e) => setField("dba", e.target.value)} />
                                {errFor("dba")}
                            </div>
                        </div>
                    </section>

                    {/* Address */}
                    <section>
                        <h3 className="text-sm font-semibold text-slate-900 mb-3 tracking-wide uppercase">Address</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="sm:col-span-2">
                                <Label htmlFor="tn-addr-line1">Street line 1</Label>
                                <Input id="tn-addr-line1" data-testid="tenant-address-line1-input"
                                       disabled={loading} value={form.address.line1}
                                       onChange={(e) => setAddrField("line1", e.target.value)} />
                                {errFor("address.line1")}
                            </div>
                            <div className="sm:col-span-2">
                                <Label htmlFor="tn-addr-line2">Street line 2 <span className="text-slate-400">(optional)</span></Label>
                                <Input id="tn-addr-line2" data-testid="tenant-address-line2-input"
                                       disabled={loading} value={form.address.line2}
                                       onChange={(e) => setAddrField("line2", e.target.value)} />
                            </div>
                            <div>
                                <Label htmlFor="tn-addr-city">City</Label>
                                <Input id="tn-addr-city" data-testid="tenant-address-city-input"
                                       disabled={loading} value={form.address.city}
                                       onChange={(e) => setAddrField("city", e.target.value)} />
                                {errFor("address.city")}
                            </div>
                            <div>
                                <Label htmlFor="tn-addr-state">State</Label>
                                <Input id="tn-addr-state" data-testid="tenant-address-state-input"
                                       disabled={loading} value={form.address.state}
                                       onChange={(e) => setAddrField("state", e.target.value)} />
                                {errFor("address.state")}
                            </div>
                            <div>
                                <Label htmlFor="tn-addr-zip">Zip</Label>
                                <Input id="tn-addr-zip" data-testid="tenant-address-zip-input"
                                       disabled={loading} value={form.address.zip}
                                       onChange={(e) => setAddrField("zip", e.target.value)}
                                       placeholder="94107" />
                                {errFor("address.zip")}
                            </div>
                            <div>
                                <Label htmlFor="tn-addr-country">Country</Label>
                                <Input id="tn-addr-country" data-testid="tenant-address-country-input"
                                       disabled={loading} value={form.address.country}
                                       onChange={(e) => setAddrField("country", e.target.value)} />
                                {errFor("address.country")}
                            </div>
                        </div>
                    </section>

                    {/* Contact */}
                    <section>
                        <h3 className="text-sm font-semibold text-slate-900 mb-3 tracking-wide uppercase">Contact</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <Label htmlFor="tn-phone">Phone</Label>
                                <PhoneInput
                                    data-testid="tenant-phone-input"
                                    disabled={loading}
                                    value={form.phone}
                                    onChange={(v) => setField("phone", v)}
                                />
                                {errFor("phone")}
                            </div>
                            <div>
                                <Label htmlFor="tn-reply-to">Reply-to email</Label>
                                <Input id="tn-reply-to" data-testid="tenant-reply-to-input"
                                       type="email" disabled={loading} value={form.reply_to_email}
                                       onChange={(e) => setField("reply_to_email", e.target.value)}
                                       placeholder="hello@allabouttrucking.com" />
                                {errFor("reply_to_email")}
                            </div>
                            <div className="sm:col-span-2">
                                <Label htmlFor="tn-website">Website</Label>
                                <Input id="tn-website" data-testid="tenant-website-input"
                                       disabled={loading} value={form.website}
                                       onChange={(e) => setField("website", e.target.value)}
                                       placeholder="https://allabouttrucking.com" />
                                {errFor("website")}
                            </div>
                        </div>
                    </section>

                    {/* Tax */}
                    <section>
                        <h3 className="text-sm font-semibold text-slate-900 mb-3 tracking-wide uppercase">Tax</h3>
                        <div className="max-w-xs">
                            <Label htmlFor="tn-ein">EIN <span className="text-slate-400">(XX-XXXXXXX)</span></Label>
                            <Input id="tn-ein" data-testid="tenant-ein-input"
                                   disabled={loading} value={form.ein}
                                   onChange={(e) => setField("ein", e.target.value)}
                                   placeholder="12-3456789" />
                            {errFor("ein")}
                            <p className="text-xs text-slate-500 mt-1">Shown on invoices when set.</p>
                        </div>
                    </section>

                    {/* Logo */}
                    <section>
                        <h3 className="text-sm font-semibold text-slate-900 mb-3 tracking-wide uppercase">Logo</h3>
                        <div className="flex items-center gap-4 flex-wrap">
                            <div
                                data-testid="tenant-logo-preview"
                                className="h-20 w-40 rounded-md border border-dashed border-slate-300 bg-slate-50 flex items-center justify-center overflow-hidden"
                            >
                                {logoSrc ? (
                                    <img
                                        src={logoSrc}
                                        alt={form.display_name || "Tenant logo"}
                                        className="max-h-full max-w-full object-contain"
                                    />
                                ) : (
                                    <ImageIcon size={28} className="text-slate-300" />
                                )}
                            </div>
                            <div className="flex flex-col gap-2">
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/png,image/jpeg,image/webp,image/svg+xml"
                                    className="hidden"
                                    onChange={onLogoChange}
                                    data-testid="tenant-logo-file-input"
                                />
                                <Button
                                    type="button"
                                    variant="outline"
                                    data-testid="tenant-logo-upload"
                                    onClick={() => fileInputRef.current?.click()}
                                    disabled={loading}
                                    className="gap-2"
                                >
                                    <Upload size={14} />
                                    {logoRecord ? "Replace logo" : "Upload logo"}
                                </Button>
                                {logoRecord && (
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        data-testid="tenant-logo-delete"
                                        onClick={onLogoDelete}
                                        disabled={loading}
                                        className="gap-2 text-red-700 hover:text-red-800 hover:bg-red-50"
                                    >
                                        <Trash2 size={14} />
                                        Delete logo
                                    </Button>
                                )}
                                <p className="text-xs text-slate-500 mt-1">
                                    PNG, JPEG, WEBP, or SVG up to 2 MB.
                                </p>
                            </div>
                        </div>
                    </section>

                    <div className="flex items-center justify-end gap-3 pt-2 border-t border-slate-100">
                        <span className="text-xs text-slate-500" data-testid="tenant-settings-dirty-hint">
                            {dirty ? "Unsaved changes" : "All changes saved"}
                        </span>
                        <Button
                            type="submit"
                            data-testid="tenant-settings-save"
                            disabled={loading || saving || !dirty}
                            className="bg-blue-900 hover:bg-blue-800"
                        >
                            {saving ? "Saving…" : "Save tenant settings"}
                        </Button>
                    </div>
                </form>
            </CardContent>
        </Card>
    );
}

export default TenantSettingsCard;
